import { useState } from 'react'
import { Plus, Settings2, Play, Square } from 'lucide-react'
import { Header } from '@/components/layout/Header'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useSplashStatus, useSplashSettings, useSaveSplashAlert, useDeleteSplashAlert, useUpdateSplashAlertsIgnore, useStartSplash, useStopSplash } from '@/hooks/useAlerts'
import { useAuth } from '@/hooks/useAuth'
import { StatsCard, ExchangeBadge, GlobalIgnoreList } from '@/components/common'
import { SplashAlertEditSheet } from '@/components/alerts/SplashAlertEditSheet'
import type { SplashAlert } from '@/types'

export default function Splash() {
  const { data: status } = useSplashStatus()
  const { data: settings, isLoading } = useSplashSettings()
  const saveAlert = useSaveSplashAlert()
  const deleteAlert = useDeleteSplashAlert()
  const updateIgnore = useUpdateSplashAlertsIgnore()
  const startSplash = useStartSplash()
  const stopSplash = useStopSplash()
  const { isAdmin } = useAuth()

  const [editingAlert, setEditingAlert] = useState<SplashAlert | null>(null)
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  const alerts = settings?.splash_alerts || []
  const isRunning = status?.running === true

  const handleAddAlert = () => {
    setEditingAlert(null)
    setIsSheetOpen(true)
  }

  const handleEditAlert = (alert: SplashAlert) => {
    setEditingAlert(alert)
    setIsSheetOpen(true)
  }

  const handleSaveAlert = async (alertData: Partial<SplashAlert>) => {
    await saveAlert.mutateAsync(alertData)
  }

  const handleDeleteAlert = async (id: string) => {
    await deleteAlert.mutateAsync(id)
  }

  const handleUpdateIgnore = async (symbols: string[]) => {
    await updateIgnore.mutateAsync(symbols)
  }

  return (
    <div className="flex flex-col">
      <Header title="Splash Alerts" />

      <div className="flex-1 space-y-6 p-6">
        {/* Header with Add Alert and Global Ignore */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant={isRunning ? 'success' : 'secondary'}>
              {isRunning ? 'Active' : 'Stopped'}
            </Badge>
            {isAdmin && (
              isRunning ? (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => stopSplash.mutate()}
                  disabled={stopSplash.isPending}
                >
                  <Square className="mr-2 h-3 w-3" />
                  {stopSplash.isPending ? 'Stopping...' : 'Stop'}
                </Button>
              ) : (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => startSplash.mutate()}
                  disabled={startSplash.isPending}
                >
                  <Play className="mr-2 h-3 w-3" />
                  {startSplash.isPending ? 'Starting...' : 'Start'}
                </Button>
              )
            )}
          </div>
          <div className="flex gap-2">
            <GlobalIgnoreList
              symbols={settings?.ignore || []}
              alertType="Splash alerts"
              onUpdate={handleUpdateIgnore}
              isUpdating={updateIgnore.isPending}
              compact
            />
            <Button variant="outline" onClick={handleAddAlert}>
              <Plus className="mr-2 h-4 w-4" />
              Add Alert
            </Button>
          </div>
        </div>

        {/* Info Banner */}
        <Card className="border-orange-500/30 bg-orange-500/10">
          <CardContent className="py-4">
            <p className="text-sm text-orange-300">
              <strong>What are Splash alerts?</strong> Detects sporadic price spikes — rapid,
              high-velocity changes (e.g. 50%+ in 5 minutes) using local aggregated trade data.
              Includes price charts and cross-exchange grouping when similar spikes are detected.
            </p>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-3">
          <StatsCard
            title="Status"
            value={isRunning ? 'Monitoring' : 'Stopped'}
            description="Price spike detection"
            status={isRunning ? 'running' : 'stopped'}
          />
          <StatsCard
            title="Active Triggers"
            value={status?.triggers_count || 0}
            description="Splash alerts triggered"
            status={isRunning ? 'running' : 'stopped'}
          />
          <StatsCard
            title="Configured Alerts"
            value={alerts.length}
            description="Active alert configurations"
          />
        </div>

        {/* Alerts List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Configured Alerts</CardTitle>
            <CardDescription>
              Splash price spike monitoring configurations
            </CardDescription>
          </CardHeader>
          <CardContent>
            {alerts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No splash alerts configured. Click "Add Alert" to create one.
              </div>
            ) : (
              <div className="space-y-3">
                {alerts.map((alert, idx) => (
                  <div
                    key={alert.id || idx}
                    className="flex items-center justify-between rounded-lg border p-4"
                  >
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{alert.name || alert.id || `Alert ${idx + 1}`}</span>
                        <Badge variant={alert.enabled !== false ? 'success' : 'secondary'}>
                          {alert.enabled !== false ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(alert.exchanges || []).map((ex) => (
                          <ExchangeBadge key={ex} exchange={ex} />
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Lookback: {alert.filters?.splash_lookback_minutes || alert.splash_lookback_minutes || 5}m
                        {' · '}
                        Threshold: {alert.filters?.splash_min_change_pct || alert.splash_min_change_pct || 50}%
                      </p>
                      {/* Tags */}
                      {(alert.tag_expression || (alert.required_tags?.length || 0) > 0 || (alert.exclude_tags?.length || 0) > 0) && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {alert.tag_expression ? (
                            <Badge variant="outline" className="text-xs font-mono">
                              {alert.tag_expression}
                            </Badge>
                          ) : (
                            <>
                              {(alert.required_tags || []).map((tag) => (
                                <Badge key={tag} className="bg-green-500/20 text-green-500 text-xs">
                                  +{tag}
                                </Badge>
                              ))}
                              {(alert.exclude_tags || []).map((tag) => (
                                <Badge key={tag} variant="destructive" className="text-xs">
                                  -{tag}
                                </Badge>
                              ))}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditAlert(alert)}
                      >
                        <Settings2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Sheet */}
      <SplashAlertEditSheet
        open={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        alert={editingAlert}
        onSave={handleSaveAlert}
        onDelete={handleDeleteAlert}
      />
    </div>
  )
}
